/**
 * The event header — the thing every other document hangs beneath.
 *
 * Covers what the Delivery Plan's *Event configuration UI* names: dates,
 * cut-offs, timezone, medical provider. Cut-offs proper depend on real
 * Coast-to-Coast distances, which is G4 and still deferred, so what is here is
 * the event-wide dwell default rather than per-checkpoint cut-off times.
 */
import { isKnownTimezone } from './time.js';
import { isFilled, isWholeNumberAtLeast, problem, } from './validation.js';
/**
 * Reception's presets, which came from a real desk rather than from theory,
 * and are the right configuration vocabulary here too.
 */
export const DWELL_PRESET_MINUTES = [20, 40, 60, 240, 360, 480];
/** Confirmed operating parameter: events run up to seven days, the Spine being the extreme. */
const MAX_EXPECTED_DAYS = 7;
const MINUTES_PER_DAY = 24 * 60;
/** Whole days, rounded up, that the event spans. Negative if the dates are backwards. */
export function eventDurationDays(event) {
    const ms = event.endsAt.getTime() - event.startsAt.getTime();
    return Math.ceil(ms / (MINUTES_PER_DAY * 60 * 1000));
}
export function validateEvent(event) {
    const found = [];
    if (!isFilled(event.name)) {
        found.push(problem('name', 'error', 'Give the event a name.'));
    }
    if (!isKnownTimezone(event.timezone)) {
        found.push(problem('timezone', 'error', 'That is not a timezone this system recognises. Use a name like Europe/London.'));
    }
    const startValid = !Number.isNaN(event.startsAt.getTime());
    const endValid = !Number.isNaN(event.endsAt.getTime());
    if (!startValid) {
        found.push(problem('startsAt', 'error', 'The start date is not a real date.'));
    }
    if (!endValid) {
        found.push(problem('endsAt', 'error', 'The end date is not a real date.'));
    }
    if (startValid && endValid) {
        if (event.endsAt.getTime() <= event.startsAt.getTime()) {
            found.push(problem('endsAt', 'error', 'The event ends before it starts.'));
        }
        else if (eventDurationDays(event) > MAX_EXPECTED_DAYS) {
            // A warning, not a block. Seven days is the longest event anyone has
            // described, not a limit the system imposes — but a run of 70 days is
            // almost always a mistyped year, and worth querying before it is saved.
            found.push(problem('endsAt', 'warning', `This event runs for ${String(eventDurationDays(event))} days. Anything past ${String(MAX_EXPECTED_DAYS)} is unusual — check the dates.`));
        }
    }
    if (!isWholeNumberAtLeast(event.defaultMaxDwellMinutes, 1)) {
        found.push(problem('defaultMaxDwellMinutes', 'error', 'The default maximum dwell must be at least one minute.'));
    }
    return found;
}
//# sourceMappingURL=event.js.map